import { type HassEntityWithService, type LightColorMode, type LightColor, temperature2rgb } from "@core";
import { LIGHT_COLOR_MODES } from "../../types/autogenerated-types-by-domain";

const modesSupportingColor: LightColorMode[] = [
  LIGHT_COLOR_MODES.HS,
  LIGHT_COLOR_MODES.XY,
  LIGHT_COLOR_MODES.RGB,
  LIGHT_COLOR_MODES.RGBW,
  LIGHT_COLOR_MODES.RGBWW,
];

const modesSupportingBrightness: LightColorMode[] = [
  ...modesSupportingColor,
  LIGHT_COLOR_MODES.COLOR_TEMP,
  LIGHT_COLOR_MODES.BRIGHTNESS,
  LIGHT_COLOR_MODES.WHITE,
];

export const lightSupportsColorMode = (entity: HassEntityWithService<"light">, mode: LightColorMode) =>
  entity.attributes.supported_color_modes?.includes(mode) || false;

export const lightIsInColorMode = (entity: HassEntityWithService<"light">) =>
  (entity.attributes.color_mode && modesSupportingColor.includes(entity.attributes.color_mode)) || false;

export const lightSupportsColor = (entity: HassEntityWithService<"light">) =>
  entity.attributes.supported_color_modes?.some((mode) => modesSupportingColor.includes(mode)) || false;

export const lightSupportsBrightness = (entity: HassEntityWithService<"light">) =>
  entity.attributes.supported_color_modes?.some((mode) => modesSupportingBrightness.includes(mode)) || false;

export const lightSupportsFavoriteColors = (entity: HassEntityWithService<"light">) =>
  lightSupportsColor(entity) || lightSupportsColorMode(entity, LIGHT_COLOR_MODES.COLOR_TEMP);

export const getLightCurrentModeRgbColor = (entity: HassEntityWithService<"light">): number[] | undefined =>
  entity.attributes.color_mode === LIGHT_COLOR_MODES.RGBWW
    ? entity.attributes.rgbww_color
    : entity.attributes.color_mode === LIGHT_COLOR_MODES.RGBW
      ? entity.attributes.rgbw_color
      : entity.attributes.rgb_color;

const COLOR_TEMP_COUNT = 4;
const DEFAULT_COLORED_COLORS = [
  { rgb_color: [127, 172, 255] }, // blue #7FACFF
  { rgb_color: [215, 150, 255] }, // purple #D796FF
  { rgb_color: [255, 158, 243] }, // pink #FF9EF3
  { rgb_color: [255, 110, 84] }, // red #FF6E54
] as LightColor[];

export const computeDefaultFavoriteColors = (stateObj: HassEntityWithService<"light">): LightColor[] => {
  const colors: LightColor[] = [];

  const supportsColorTemp = lightSupportsColorMode(stateObj, LIGHT_COLOR_MODES.COLOR_TEMP);

  const supportsColor = lightSupportsColor(stateObj);

  if (supportsColorTemp) {
    const min = stateObj.attributes.min_color_temp_kelvin!;
    const max = stateObj.attributes.max_color_temp_kelvin!;
    const step = (max - min) / (COLOR_TEMP_COUNT - 1);

    for (let i = 0; i < COLOR_TEMP_COUNT; i++) {
      colors.push({
        color_temp_kelvin: Math.round(min + step * i),
      });
    }
  } else if (supportsColor) {
    const min = 2000;
    const max = 6500;
    const step = (max - min) / (COLOR_TEMP_COUNT - 1);

    for (let i = 0; i < COLOR_TEMP_COUNT; i++) {
      colors.push({
        rgb_color: temperature2rgb(Math.round(min + step * i)),
      });
    }
  }

  if (supportsColor) {
    colors.push(...DEFAULT_COLORED_COLORS);
  }

  return colors;
};
